import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { FaPhone, FaWhatsapp, FaChevronRight } from "react-icons/fa";
import seoData from "../assets/data/seo.json";

import { CONTACTUS_API } from "../hooks/Apis";

import FooterLogo1 from "../assets/icons/FooterLogo1.webp";
import FooterLogo2 from "../assets/icons/FooterLogo2.webp";
import FooterLogo3 from "../assets/icons/FooterLogo3.webp";
import FooterLogo4 from "../assets/icons/FooterLogo4.webp";
import chatbotcsd_thanking from "../assets/icons/chatbotcsd_thanking.webp";

const Contactus = () => {
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const seo = seoData["contactus"] || {};
  const { title, description, keywords, canonical, ogImage } = seo;

  // Real estate subject options for dropdown
  const subjectOptions = [
    "Buy a Luxury Villa / Jetty Mayfair",
    "Schedule a Private Property Tour",
    "List / Sell My Property",
    "Real Estate Investment Consultation",
    "General Inquiry",
  ];

  // FAQ data tailored to Jetty Estates
  const faqs = [
    {
      question: "How quickly can I schedule a private property viewing?",
      answer:
        "We arrange private viewings within 24 hours of your inquiry. For featured developments like Jetty Mayfair, VIP appointments are available 7 days a week.",
    },
    {
      question: "What information should I prepare before listing my property?",
      answer:
        "Having basic details such as property age, square footage, existing floor plans, and recent renovations helps us provide a comprehensive market valuation during our initial consultation.",
    },
    {
      question: "Do you offer virtual tours for overseas clients?",
      answer:
        "Yes! We provide interactive 3D virtual walkthroughs, ultra-HD video tours, and live video walk-throughs guided by our senior estate specialists.",
    },
    {
      question: "What sets Jetty Mayfair apart from other developments?",
      answer:
        "Jetty Mayfair combines architectural elegance, private waterfront access, state-of-the-art smart home automation, and 24/7 white-glove concierge services in an ultra-exclusive community.",
    },
    {
      question: "Do you assist international buyers with legal and financial guidance?",
      answer:
        "Absolutely. Our team collaborates with premier real estate attorneys, wealth managers, and tax consultants to make international property acquisition smooth and compliant.",
    },
    {
      question: "Are there dedicated advisors for investment portfolios?",
      answer:
        "Yes, our real estate advisory team builds customized investment proposals targeting high capital growth and lucrative rental yield opportunities.",
    },
  ];

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSection = useMemo(
    () => (
      <div className="space-y-3">
        {faqs.map((faq, index) => (
          <div key={index} className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <button
              onClick={() => toggle(index)}
              className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-slate-50 transition-colors"
            >
              <span className="text-[14px] md:text-[16px] font-bold text-slate-800">{faq.question}</span>
              <FaChevronRight
                className={`text-slate-400 transition-transform duration-300 ${
                  openIndex === index ? "rotate-90 text-secondary" : ""
                }`}
              />
            </button>
            {openIndex === index && (
              <div className="p-4 bg-slate-50 text-[14px] md:text-[15px] text-slate-600 border-t border-gray-100 leading-relaxed">
                {faq.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    ),
    [openIndex, faqs]
  );

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;

    setLoading(true);
    try {
      const response = await fetch(CONTACTUS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (result.success || response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        alert(result.error || "Failed to send inquiry. Please try again.");
      }
    } catch (error) {
      // Fallback for offline/local test
      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white my-6 mt-20 md:mt-24">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        {canonical && <link rel="canonical" href={canonical} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
      </Helmet>

      {/* Hero & Form Section */}
      <section className="relative max-w-7xl mx-auto px-4 pt-8 md:pt-16 pb-12 md:pb-24 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl -z-10 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl -z-10 delay-700"></div>

        <div className="grid lg:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left Side: Hero Text & Trust Markers (5 Columns) */}
          <div className="lg:col-span-5 space-y-8 relative z-10 order-2 lg:order-1 mt-12 lg:mt-0">
            <div>
              <span className="inline-block px-4 py-1.5 mb-6 text-[12px] font-bold tracking-widest text-primary uppercase bg-secondary/20 rounded-full">
                Spaces Where Life Unfolds
              </span>
              <h1 className="text-[36px] md:text-[50px] font-extrabold text-slate-900 mb-4 leading-tight">
                Find Your Luxury <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-slate-800 to-amber-700">
                  Dream Property
                </span>
              </h1>
              <p className="text-[16px] md:text-[18px] text-slate-600 leading-relaxed font-medium">
                Join 100+ discerning buyers and investors who curated their premium residences with Jetty Estates. Get a personalized consultation in 24 hours.
              </p>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-6 py-4 border-y border-slate-100">
              {[
                { val: "100+", lab: "Luxury Estates" },
                { val: "100%", lab: "Satisfaction" },
                { val: "24/7", lab: "Support" },
              ].map((stat, i) => (
                <div key={i} className="flex flex-col">
                  <span className="text-[24px] font-extrabold text-slate-900">
                    {stat.val}
                  </span>
                  <span className="text-[11px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                    {stat.lab}
                  </span>
                </div>
              ))}
            </div>

            {/* Premium Trust Quote */}
            <div className="p-8 bg-gradient-to-br from-primary via-[#2b2d2e] to-slate-900 rounded-3xl text-white relative overflow-hidden shadow-2xl shadow-primary/20 border border-white/10 group hover:border-secondary/40 transition-colors duration-500 mt-2">
              {/* Animated Glow Effects */}
              <div className="absolute -top-20 -right-20 w-48 h-48 bg-secondary/20 rounded-full blur-3xl group-hover:bg-secondary/30 transition-colors duration-700"></div>
              <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl group-hover:bg-amber-500/20 transition-colors duration-700"></div>

              {/* Large Quote Mark */}
              <span className="absolute top-4 left-6 text-[100px] text-white/5 font-serif leading-none select-none pointer-events-none">
                "
              </span>

              <div className="relative z-10">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg
                      key={star}
                      className="w-4 h-4 text-secondary fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                <p className="text-slate-200 mb-6 text-[15px] md:text-[16px] leading-relaxed font-medium">
                  "Jetty Estates made securing our waterfront villa at Jetty Mayfair an absolute pleasure. Their attention to detail and market expertise are{" "}
                  <span className="text-secondary font-bold">
                    unmatched in luxury real estate
                  </span>."
                </p>

                <div className="flex items-center gap-4 pt-5 border-t border-white/10">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-secondary to-amber-600 p-[2px]">
                    <div className="w-full h-full bg-primary rounded-full flex items-center justify-center font-bold text-[18px] text-secondary">
                      J
                    </div>
                  </div>
                  <div>
                    <p className="font-bold text-[14px] text-white">
                      Alexander Vance
                    </p>
                    <p className="text-[11px] text-secondary font-bold uppercase tracking-widest mt-0.5">
                      Mayfair Estate Owner
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Direct Connect Buttons */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <a
                href="https://wa.me/13105550198"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-bold transition-all shadow-lg shadow-green-100"
              >
                <FaWhatsapp /> WhatsApp
              </a>
              <a
                href="tel:+13105550198"
                className="flex items-center justify-center gap-2 py-3 bg-primary hover:bg-primary/90 text-secondary rounded-2xl font-bold transition-all shadow-lg shadow-gray-200"
              >
                <FaPhone /> Call Now
              </a>
            </div>
          </div>

          {/* Right Side: The Form (7 Columns) */}
          <div className="lg:col-span-7 bg-white rounded-3xl shadow-2xl shadow-slate-200/50 border border-slate-100 p-4 md:p-10 relative overflow-hidden group h-full order-1 lg:order-2">
            {/* Glossy overlay effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-tr from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

            <div className="relative z-10">
              {/* Registered On Badges */}
              <div className="mb-4 pb-4 md:mb-6 md:pb-6 border-b border-slate-100">
                <p className="text-slate-400 text-center text-[11px] font-bold uppercase tracking-widest mb-3">
                  Recognized & Certified Partner
                </p>
                <div className="flex justify-center items-center gap-3 md:gap-4 flex-wrap">
                  {[FooterLogo1, FooterLogo2, FooterLogo3, FooterLogo4].map(
                    (logo, i) => (
                      <img
                        key={i}
                        src={logo}
                        alt={`footer_logo_${i}`}
                        className="h-8 md:h-10 hover:scale-105 transition-transform duration-300 grayscale hover:grayscale-0 rounded-md shadow-sm"
                      />
                    )
                  )}
                </div>
              </div>

              <h2 className="text-[24px] md:text-[28px] font-bold text-slate-900 mb-1 md:mb-2">
                Request a Free Property Quote
              </h2>
              <p className="text-[14px] md:text-[16px] text-slate-500 mb-5 md:mb-8 font-medium">
                Step into the future of luxury living. Fill out the form below.
              </p>

              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-10 animate-fade-in text-center">
                  <img
                    src={chatbotcsd_thanking}
                    alt="Thank You"
                    className="w-48 h-48 md:w-56 md:h-56 object-contain mb-6 drop-shadow-2xl hover:scale-105 transition-transform duration-500"
                  />
                  <h3 className="text-[22px] md:text-[26px] font-bold text-slate-900 mb-2">
                    Thank you for reaching out!
                  </h3>
                  <p className="text-[14px] md:text-[15px] text-slate-500 font-medium max-w-xs mb-6 mx-auto">
                    Our estate specialists will contact you within 24 hours.
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsSubmitted(false)}
                    className="text-primary font-bold hover:underline text-[14px]"
                  >
                    Submit another inquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-3 md:space-y-5"
                >
                  <input
                    type="text"
                    name="honeypot"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    className="hidden"
                  />

                  <div className="grid md:grid-cols-2 gap-3 md:gap-5">
                    <div className="space-y-1 md:space-y-1.5">
                      <label className="text-[11px] md:text-[12px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 md:px-4 md:py-3 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl focus:ring-4 focus:ring-secondary/20 focus:border-secondary outline-none transition-all font-medium text-slate-900 placeholder:text-slate-300 text-[14px] md:text-[16px]"
                        required
                      />
                    </div>
                    <div className="space-y-1 md:space-y-1.5">
                      <label className="text-[11px] md:text-[12px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 md:px-4 md:py-3 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl focus:ring-4 focus:ring-secondary/20 focus:border-secondary outline-none transition-all font-medium text-slate-900 placeholder:text-slate-300 text-[14px] md:text-[16px]"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3 md:gap-5">
                    <div className="space-y-1 md:space-y-1.5">
                      <label className="text-[11px] md:text-[12px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+1 (555) 000-0000"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 md:px-4 md:py-3 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl focus:ring-4 focus:ring-secondary/20 focus:border-secondary outline-none transition-all font-medium text-slate-900 placeholder:text-slate-300 text-[14px] md:text-[16px]"
                        required
                      />
                    </div>
                    <div className="space-y-1 md:space-y-1.5">
                      <label className="text-[11px] md:text-[12px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                        Interested In
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 md:px-4 md:py-3 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl focus:ring-4 focus:ring-secondary/20 focus:border-secondary outline-none transition-all font-medium text-slate-900 text-[14px] md:text-[16px]"
                        required
                      >
                        <option value="">Select Service / Interest</option>
                        {subjectOptions.map((option, index) => (
                          <option key={index} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1 md:space-y-1.5">
                    <label className="text-[11px] md:text-[12px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                      Property Requirements & Message
                    </label>
                    <textarea
                      name="message"
                      placeholder="Briefly describe your preferred location, budget, or inquiry details..."
                      rows="3"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 md:px-4 md:py-3 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl focus:ring-4 focus:ring-secondary/20 focus:border-secondary outline-none transition-all font-medium text-slate-900 placeholder:text-slate-300 resize-none text-[14px] md:text-[16px]"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full group relative flex items-center justify-center py-3 md:py-4 px-6 md:px-8 rounded-xl md:rounded-2xl font-bold text-[16px] md:text-[18px] tracking-wide transition-all duration-300 overflow-hidden mt-2 md:mt-4 ${
                      loading
                        ? "bg-slate-300 cursor-not-allowed text-slate-500"
                        : "bg-primary hover:bg-slate-800 text-secondary shadow-xl shadow-primary/20 hover:shadow-primary/40 active:scale-95"
                    }`}
                  >
                    <span className="relative z-10 transition-transform group-hover:scale-105">
                      {loading ? "Processing..." : "Submit Inquiry"}
                    </span>
                    {!loading && (
                      <FaChevronRight className="relative z-10 ml-2 group-hover:translate-x-2 transition-transform text-secondary" />
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-slate-50 py-24 px-4 overflow-hidden relative">
        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-96 h-96 bg-secondary/10 rounded-full blur-3xl -z-0"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-[30px] md:text-[36px] font-extrabold text-center text-slate-900 mb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-slate-500 mb-12 font-medium">
            Everything you need to know about purchasing or listing with Jetty Estates.
          </p>
          {faqSection}
        </div>
      </section>
    </div>
  );
};

export default Contactus;
